import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Logo } from './Logo'
import { ThemeToggle } from './ThemeToggle'
import { ShareButton } from './ShareButton'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'

interface TopBarProps {
  userEmail: string
  onSignOut: () => void
  onOpenSettings: () => void
  /** T-368: the Admin entry point only exists for members of the Cognito `admins` group. */
  isAdmin?: boolean
  onOpenAdmin?: () => void
  /**
   * T-500: builds the shareable authed deep-link for the current location, evaluated at click time.
   * When present, a Share control is shown in the header; omitted (e.g. no map context) → no control.
   */
  getShareUrl?: () => string
}

/**
 * TopBar — app header. The account area is a Radix Popover (finding h): using the shared
 * ui/popover primitive gives proper a11y semantics for free — Escape closes, focus returns
 * to the trigger, and outside-clicks dismiss — which the previous hand-rolled toggle menu
 * did not handle. Controlled `open` lets a menu-item click close the popover as it fires.
 */
export function TopBar({
  userEmail,
  onSignOut,
  onOpenSettings,
  isAdmin = false,
  onOpenAdmin,
  getShareUrl,
}: TopBarProps) {
  const [open, setOpen] = useState(false)

  return (
    <header className="h-14 bg-card border-b border-border z-20 flex items-center justify-between px-4">
      <a href="https://www.optiyield.ai" aria-label="OptiYield home" className="flex items-center">
        {/* Full wordmark lockup on ≥sm; square icon on mobile/collapsed. */}
        <Logo variant="full" className="hidden sm:block" />
        <Logo variant="icon" className="h-8 sm:hidden" />
      </a>

      <div className="flex items-center gap-1">
        {/* T-500: share the current location as an authed deep-link (only when a builder is wired). */}
        {getShareUrl ? <ShareButton getShareUrl={getShareUrl} /> : null}
        {/* T-156: theme toggle sits just left of the account menu. */}
        <ThemeToggle />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            aria-label="Account menu"
            className="flex items-center gap-2 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <span className="truncate max-w-[160px]">{userEmail}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          </PopoverTrigger>
          <PopoverContent align="end" className="w-44 p-1">
            <button
              type="button"
              className="w-full rounded-sm px-3 py-2 text-left text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
              onClick={() => {
                setOpen(false)
                onOpenSettings()
              }}
            >
              Settings
            </button>
            {isAdmin && onOpenAdmin ? (
              <button
                type="button"
                className="w-full rounded-sm px-3 py-2 text-left text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
                onClick={() => {
                  setOpen(false)
                  onOpenAdmin()
                }}
              >
                Admin
              </button>
            ) : null}
            <button
              type="button"
              className="w-full rounded-sm px-3 py-2 text-left text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
              onClick={() => {
                setOpen(false)
                onSignOut()
              }}
            >
              Sign out
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  )
}
